import React, { useState, useEffect, useCallback, useImperativeHandle } from "react";
import "./Alert.css"; // Import the new CSS file

// Wrap the functional component with React.forwardRef
const Alert = React.forwardRef(({ message, duration = 3000 }, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentMessage, setCurrentMessage] = useState("");
  const [hideTimeoutId, setHideTimeoutId] = useState(null);

  // Function to show the alert with a new message
  const showAlert = useCallback(
    (msg) => {
      // Clear any existing hide timeout to prevent premature hiding
      if (hideTimeoutId) {
        clearTimeout(hideTimeoutId);
      }
      setCurrentMessage(msg);
      setIsVisible(true);

      // Set a new timeout to hide the alert after 'duration' milliseconds
      const id = setTimeout(() => {
        setIsVisible(false);
        setHideTimeoutId(null); // Clear the ID after hiding
      }, duration);
      setHideTimeoutId(id);
    },
    [duration, hideTimeoutId]
  );

  // Use useImperativeHandle to expose showAlert via the forwarded ref
  // The 'ref' argument here is the one passed from the parent (AnotherComponent)
  useImperativeHandle(ref, () => ({
    showAlert,
  }));

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutId) {
        clearTimeout(hideTimeoutId);
      }
    };
  }, [hideTimeoutId]);

  // Combine base class with dynamic visibility classes
  // The background color is now entirely handled by .alert-container in Alert.css
  const alertClasses = `alert-container ${isVisible ? "alert-visible" : "alert-hidden"}`;

  return <div className={alertClasses}>{currentMessage}</div>;
});

export default Alert;
